import React, { useState, useMemo, useEffect } from "react";
import {
  Eye,
  Search,
  Calendar,
  Users,
  FileDown,
  RotateCcw,
  X,
} from "lucide-react";
import "./QuizAssesmentHistory.scss";
import { useDispatch, useSelector } from "react-redux";
import { allAssignmentHistory } from "../../../../Redux/Teacher_Panel/Teacher_Resource/resource.action";
import { profileDetails } from "../../../../Redux/profile/profile.action";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../../../utils/utils";

const ROWS_OPTIONS = [5, 10, 20];

const getAssignmentSets = (item) => {
  const raw = Array.isArray(item?.set_data)
    ? item.set_data
    : Array.isArray(item?.sets)
      ? item.sets
      : [];
  return raw
    .map((s, i) => ({
      // setId: s?.set_id ?? null,
      number: s?.set_number ?? i + 1,
      questions: Array.isArray(s?.questions) ? s.questions : [],
    }))
    .sort((a, b) => a.number - b.number);
};

const TYPE_LABEL = {
  mcq: "MCQ",
  fill_in_the_blank: "FIB",
  short_answer: "SHORT",
};
const TYPE_ORDER = { MCQ: 0, FIB: 1, SHORT: 2 };

const formatQuestion = (q, fallbackType) => ({
  type:
    TYPE_LABEL[q?.question_type] ||
    fallbackType ||
    String(q?.question_type || "Q").toUpperCase(),
  text: q?.question_text ?? q?.question ?? "",
  options: Array.isArray(q?.options) ? q.options : [],
  correct: q?.correct_answer,
  answer: q?.correct_answer,
  explanation: q?.explanation,
  marks: q?.max_marks ?? 1,
});

const sortByType = (list) =>
  list
    .map((q, i) => ({ q, i }))
    .sort(
      (a, b) =>
        (TYPE_ORDER[a.q.type] ?? 3) - (TYPE_ORDER[b.q.type] ?? 3) || a.i - b.i,
    )
    .map((x) => x.q);

const isCorrectOption = (opt, idx, correct) => {
  if (correct === undefined || correct === null) return false;
  const c = String(correct).trim();
  if (String(opt).trim() === c) return true;
  return (
    /^[A-Da-d]$/.test(c) && String.fromCharCode(65 + idx) === c.toUpperCase()
  );
};

const summarizeQuestionCount = (sets, assignmentData) => {
  if (sets.length) {
    const counts = sets.map((s) => s.questions.length);
    const min = Math.min(...counts);
    const max = Math.max(...counts);
    return min === max ? String(max) : `${min}–${max}`;
  }
  const d = assignmentData || {};
  return String(
    (d.mcq?.length || 0) +
      (d.short_answer?.length || 0) +
      (d.fill_in_the_blank?.length || 0),
  );
};

const QuizAssesmntHistory = () => {
  const nav = useNavigate();
  const dispatch = useDispatch();

  const [selectedTest, setSelectedTest] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterClass, setFilterClass] = useState("All");
  const [filterDivision, setFilterDivision] = useState("All");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [detailSets, setDetailSets] = useState([]);
  const [activeSetNumber, setActiveSetNumber] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const allAssignmentHistoryStore = useSelector(
    (state) => state?.CreateResourceManager?.assignment_history,
  );

  useEffect(() => {
    dispatch(allAssignmentHistory());
    dispatch(profileDetails());
  }, [dispatch]);

  useEffect(() => {
    if (allAssignmentHistoryStore?.assignments) {
      const mapped = allAssignmentHistoryStore.assignments.map((item) => {
        const sets = getAssignmentSets(item);
        return {
          id: item.assignment_id,
          subject: item.subject ? item.subject.toUpperCase() : "—",
          topic:
            Array.isArray(item.chapter) && item.chapter.length
              ? item.chapter[0]
              : "—",
          class: item.class ?? "—",
          div: item.division ?? "—",
          date: item.date ?? item.created_at?.slice(0, 10) ?? "—",
          time: item.start_time
            ? new Date(item.start_time).toLocaleTimeString("en-IN", {
                hour: "2-digit",
                minute: "2-digit",
                hour12: true,
              })
            : "—",
          duration: item.duration_minutes ?? "—",
          sets,
          setCount: sets.length,
          questionLabel: summarizeQuestionCount(sets, item?.assignment_data),
          questions: item?.assignment_data || {},
        };
      });
      const reverseData = mapped?.reverse();
      setHistoryData(reverseData);
    }
  }, [allAssignmentHistoryStore]);

  const availableClasses = useMemo(() => {
    const classes = new Set();
    historyData.forEach((item) => {
      if (item.class && item.class !== "—") {
        classes.add(String(item.class));
      }
    });
    return Array.from(classes).sort((a, b) => Number(a) - Number(b));
  }, [historyData]);

  const availableDivisions = useMemo(() => {
    const divisions = new Set();
    historyData.forEach((item) => {
      if (item.div && item.div !== "—") {
        divisions.add(String(item.div));
      }
    });
    return Array.from(divisions).sort();
  }, [historyData]);

  const filteredData = useMemo(() => {
    setCurrentPage(1);
    return historyData.filter((item) => {
      const matchesSearch =
        item?.topic?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        item?.subject?.toLowerCase()?.includes(searchTerm?.toLowerCase());
      const matchesClass =
        filterClass === "All" || String(item.class) === String(filterClass);
      const matchesDivision =
        filterDivision === "All" || String(item.div) === String(filterDivision);
      const itemDate = new Date(item.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate) : null;
      let matchesDate = true;
      if (start && end) matchesDate = itemDate >= start && itemDate <= end;
      else if (start) matchesDate = itemDate >= start;
      else if (end) matchesDate = itemDate <= end;
      return matchesSearch && matchesClass && matchesDivision && matchesDate;
    });
  }, [
    searchTerm,
    filterClass,
    filterDivision,
    startDate,
    endDate,
    historyData,
  ]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const showingFrom =
    filteredData.length === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const showingTo = Math.min(currentPage * rowsPerPage, filteredData.length);

  // Set shown in the preview drawer (falls back to the first one).
  const activeSet =
    detailSets.find((s) => s.number === activeSetNumber) ||
    detailSets[0] ||
    null;
  const activeQuestions = activeSet?.questions || [];

  const handlePreview = (e, test) => {
    e.preventDefault();
    let formattedSets;
    if (test.sets?.length) {
      // New format: every set has its own questions.
      formattedSets = test.sets.map((s) => ({
        number: s.number,
        setId: s.setId,
        questions: sortByType(s.questions.map((q) => formatQuestion(q))),
      }));
    } else {
      // Older assignments: a single paper stored in `assignment_data`.
      const qData = test.questions || {};
      formattedSets = [
        {
          number: 1,
          setId: null,
          questions: [
            ...(qData.mcq || []).map((q) => formatQuestion(q, "MCQ")),
            ...(qData.fill_in_the_blank || []).map((q) =>
              formatQuestion(q, "FIB"),
            ),
            ...(qData.short_answer || []).map((q) =>
              formatQuestion(q, "SHORT"),
            ),
          ],
        },
      ];
    }
    setDetailSets(formattedSets);
    setActiveSetNumber(formattedSets[0].number); // always open on the first set
    setSelectedTest(test);
  };

  //---------------------------------------export csv---------------------------------------------

  const splitDateTime = (isoString) => {
    if (!isoString) return { date: "", time: "" };

    const dateObj = new Date(isoString);

    const date = dateObj.toLocaleDateString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const time = dateObj.toLocaleTimeString("en-IN", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return { date, time };
  };

  const exportToCSV = () => {
    if (
      !filteredData?.assignments?.length &&
      !allAssignmentHistoryStore?.assignments?.length
    ) {
      alert("No data available");
      return;
    }

    const rows = [];

    (allAssignmentHistoryStore || filteredData).assignments?.forEach((item) => {
      const start = splitDateTime(item.start_time);
      const end = splitDateTime(item.end_time);
      const itemSets = getAssignmentSets(item);
      const firstSetQs = itemSets[0]?.questions;
      const countOf = (questionType, key) =>
        firstSetQs
          ? firstSetQs.filter((q) => q?.question_type === questionType).length
          : item.assignment_data?.[key]?.length || 0;

      rows.push({
        Assignment_ID: item.assignment_id,
        Class: item.class,
        Division: item.division,
        Subject: item.subject,
        Chapter: item.chapter?.join(", "),
        Duration_Minutes: item.duration_minutes,
        Number_Of_Questions: item.number_of_questions,
        Number_Of_Sets: itemSets.length,
        // Set_IDs: itemSets
        //   .map((s) => s.setId)
        //   .filter((id) => id !== null && id !== undefined)
        //   .join("; "),
        Start_Date: start.date,
        Start_Time: start.time,
        End_Date: end.date,
        End_Time: end.time,
        Is_Draft: item.is_draft ? "Yes" : "No",
        Created_At: formatDate(item.created_at),

        MCQ_Count: countOf("mcq", "mcq"),
        Short_Answer_Count: countOf("short_answer", "short_answer"),
        Fill_In_The_Blank_Count: countOf(
          "fill_in_the_blank",
          "fill_in_the_blank",
        ),
      });
    });

    // Convert JSON to CSV
    const headers = Object.keys(rows[0]).join(",");

    const csvRows = rows.map((row) =>
      Object.values(row)
        .map((value) => `"${String(value).replace(/"/g, '""')}"`)
        .join(","),
    );

    const csvContent = [headers, ...csvRows].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "assignments+history.csv");

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="history-container">
      {/* Header */}
      <div className="header-section">
        <div className="title-box">
          <h2>
            <span className="back-arrow" onClick={() => nav("/create-quiz")}>
              ←
            </span>
            Assignment History
          </h2>
          <p className="subtitle">
            Complete record of teacher-created assignments
          </p>
        </div>
        <div className="header-actions">
          <div className="results-counter">
            <span className="count-label">Total:</span>
            <span className="count-value">{filteredData.length}</span>
          </div>
          <button
            className="btn btn-dark d-flex align-items-center gap-2"
            onClick={exportToCSV}
          >
            <FileDown size={18} /> Export CSV
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="filter-bar advanced">
        <div className="filter-group">
          <label>
            <Search size={12} /> SEARCH TOPIC
          </label>
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>
            <Users size={12} /> CLASS
          </label>
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
          >
            <option value="All">All Classes</option>
            {availableClasses.map((cls) => (
              <option key={cls} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>
            <Users size={12} /> DIVISION
          </label>
          <select
            value={filterDivision}
            onChange={(e) => setFilterDivision(e.target.value)}
          >
            <option value="All">All Divisions</option>
            {availableDivisions.map((div) => (
              <option key={div} value={div}>
                {div}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>
            <Calendar size={12} /> FROM
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>
            <Calendar size={12} /> TO
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="reset-action">
          <button
            className="reset-btn-icon"
            title="Reset Filters"
            onClick={() => {
              setSearchTerm("");
              setFilterClass("All");
              setFilterDivision("All");
              setStartDate("");
              setEndDate("");
            }}
          >
            <RotateCcw size={18} />
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="table-wrapper">
        <table className="history-table">
          <thead>
            <tr>
              <th>SUBJECT &amp; TOPIC</th>
              <th>CLASS</th>
              <th>DIV</th>
              <th>DATE</th>
              <th>TIME</th>
              <th>DURATION</th>
              <th>QUES.</th>
              <th>SETS</th>
              {/* <th>SET IDS</th> */}
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length > 0 ? (
              paginatedData.map((test) => (
                <tr key={test.id}>
                  <td className="col-subject">
                    <span className="subject-text">{test.subject}</span>
                    <span className="topic-text">{test.topic}</span>
                  </td>
                  <td>
                    <strong>{test.class}</strong>
                  </td>
                  <td>
                    <strong>{test.div}</strong>
                  </td>
                  <td>{test.date}</td>
                  <td>{test.time}</td>
                  <td>
                    {test.duration === "—" ? "—" : `${test.duration} min`}
                  </td>
                  <td>
                    <span
                      className="badge-qs"
                      title={
                        test.setCount > 1 ? "Questions in each set" : undefined
                      }
                    >
                      {test.questionLabel} Qs
                      {test.setCount > 1 ? " / set" : ""}
                    </span>
                  </td>
                  <td>
                    {test.setCount > 0 ? (
                      <span className="badge-qs">
                        {test.setCount} {test.setCount === 1 ? "Set" : "Sets"}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  {/* <td>
                    {test.setCount > 0 ? (
                      <div
                        className="set-id-list"
                        style={{ display: "flex", flexWrap: "wrap", gap: 4 }}
                      >
                        {test.sets.map((s) => (
                          <span
                            key={s.setId ?? s.number}
                            className="set-id-chip"
                            title={`Set ${s.number}`}
                            style={{
                              fontSize: 11,
                              fontWeight: 600,
                              padding: "2px 8px",
                              borderRadius: 999,
                              background: "#eef2ff",
                              color: "#3730a3",
                              whiteSpace: "nowrap",
                            }}
                          >
                            S{s?.number} 
                          </span>
                        ))}
                      </div>
                    ) : (
                      "—"
                    )}
                  </td> */}
                  <td className="col-action">
                    <div className="action-btns">
                      <button
                        className="btn-icon preview"
                        onClick={(e) => handlePreview(e, test)}
                        title="Preview"
                      >
                        <Eye size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="no-results">
                  <Search size={30} />
                  <p>No matching records found.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Pagination Footer */}
        <div className="table-footer">
          <span className="showing-info">
            Showing {showingFrom}–{showingTo} of {filteredData.length}
          </span>

          <div className="pagination-controls">
            <button
              className="pg-btn"
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              title="First page"
            >
              «
            </button>
            <button
              className="pg-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            <span className="page-text">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="pg-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
            <button
              className="pg-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              title="Last page"
            >
              »
            </button>
          </div>

          <div className="rows-selector">
            <span>Rows:</span>
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              {ROWS_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* PREVIEW DRAWER */}
      {selectedTest && (
        <>
          <div
            className="drawer-overlay"
            onClick={() => setSelectedTest(null)}
          />
          <div className="preview-drawer">
            <div className="preview-drawer-header">
              <h4>Preview: {selectedTest.topic}</h4>
              <button
                className="close-btn"
                onClick={() => setSelectedTest(null)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="preview-drawer-body">
              {/* Assignment details */}
              <p
                className="preview-meta"
                style={{ fontSize: 12, color: "#64748b", margin: "0 0 12px" }}
              >
                {/* ID #{selectedTest.id} */}
                {" · "}Class {selectedTest.class}-{selectedTest.div}
                {" · "}
                {selectedTest.duration === "—"
                  ? "—"
                  : `${selectedTest.duration} min`}
                {selectedTest.setCount > 0 &&
                  ` · ${selectedTest.setCount} ${selectedTest.setCount === 1 ? "set" : "sets"}`}
              </p>

              {/* Set switcher: only for assignments that have sets (Set 1 first) */}
              {selectedTest.setCount > 0 && (
                <div
                  className="set-tabs"
                  role="group"
                  aria-label="Select question set"
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 8,
                    marginBottom: 16,
                  }}
                >
                  {detailSets.map((s) => {
                    const isActive = s.number === activeSet?.number;
                    return (
                      <button
                        key={s.setId ?? s.number}
                        type="button"
                        className={`btn btn-sm fw-bold ${isActive ? "btn-primary" : "btn-outline-primary"}`}
                        aria-pressed={isActive}
                        onClick={() => setActiveSetNumber(s.number)}
                      >
                        Set {s.number}
                        {/* {s.setId !== null && s.setId !== undefined && (
                          <small style={{ opacity: 0.75 }}> · #{s.setId}</small>
                        )} */}
                      </button>
                    );
                  })}
                </div>
              )}

              {activeQuestions.length === 0 ? (
                <p className="no-q-msg">
                  {selectedTest.setCount > 0
                    ? `No questions found in Set ${activeSet?.number}.`
                    : "No questions found."}
                </p>
              ) : (
                activeQuestions.map((q, i) => (
                  <div key={i} className="q-card">
                    <div className="q-card-top">
                      <span className="q-type-badge">{q.type}</span>
                      <span className="q-mark">
                        {q.marks} {Number(q.marks) === 1 ? "Mark" : "Marks"}
                      </span>
                    </div>
                    <p className="q-text">
                      {i + 1}. {q.text}
                    </p>
                    {q.type === "MCQ" && (
                      <div className="options-grid">
                        {q.options.map((opt, idx) => (
                          <div
                            key={idx}
                            className={`option-item ${isCorrectOption(opt, idx, q.correct) ? "correct" : ""}`}
                          >
                            {opt} {isCorrectOption(opt, idx, q.correct) && "✓"}
                          </div>
                        ))}
                      </div>
                    )}
                    {q.type !== "MCQ" && q.answer && (
                      <div className="answer-box">
                        <small>Correct Answer:</small>
                        <span>{q?.answer}</span>
                      </div>
                    )}
                    {q.explanation && (
                      <div className="explanation">
                        <strong>Explanation:</strong> {q.explanation}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default QuizAssesmntHistory;
